import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { cn } from '@/lib/utils/cn';
import {
  LayoutDashboard,
  Users,
  Activity,
  Settings,
  Shield,
  UserCog,
  Mail,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const navigation = [
  { labelKey: 'nav.dashboard', href: '/dashboard', icon: LayoutDashboard },
];

const adminNavigation = [
  { labelKey: 'nav.clients', href: '/admin/tenants', icon: Users },
  { labelKey: 'nav.users', href: '/admin/users', icon: UserCog },
  { labelKey: 'nav.agents', href: '/admin/agents', icon: Activity },
  { labelKey: 'nav.settings', href: '/admin/settings', icon: Settings },
  { labelKey: 'nav.emails', href: '/admin/emails', icon: Mail },
];

export function Sidebar({ mobileOpen = false, onClose = () => {} }: { mobileOpen?:boolean; onClose?:()=>void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (!mobileOpen) { if (element.open) element.close(); return; }
    element.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const desktop = window.matchMedia('(min-width: 1024px)');
    const resized = () => { if (desktop.matches) onClose(); };
    desktop.addEventListener('change', resized);
    return () => {
      document.body.style.overflow = previousOverflow;
      desktop.removeEventListener('change', resized);
      if (element.open) element.close();
    };
  }, [mobileOpen, onClose]);
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const content = (mobile:boolean) => (
      <div className="flex h-full min-h-0 flex-col flex-1 bg-white border-r border-gray-200">
        {/* Logo */}
        <div className="flex h-20 shrink-0 items-center justify-between border-b border-slate-800 bg-slate-900 px-5">
          <img
            src="/brand/abgsystems-white.png"
            alt="ABG Systems"
            className={cn('h-16 w-auto object-contain', mobile ? 'max-w-[calc(100%-3.25rem)]' : 'max-w-full')}
          />
          {mobile && <button type="button" onClick={onClose} aria-label={t('nav.closeMenu')} className="ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white focus-visible:ring-2 focus-visible:ring-white"><X className="h-5 w-5" /></button>}
        </div>

        {/* Navigation */}
        <nav aria-label={t('nav.mainMenu')} className="min-h-0 flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.labelKey}
              to={item.href}
              onClick={mobile ? onClose : undefined}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )
              }
            >
              <item.icon className="w-5 h-5 mr-3" />
              {t(item.labelKey)}
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className="pt-4 mt-4 border-t border-gray-200">
                <div className="flex items-center px-3 mb-2">
                  <Shield className="w-4 h-4 text-gray-400" />
                  <span className="ml-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Admin
                  </span>
                </div>
                {adminNavigation.map((item) => (
                  <NavLink
                    key={item.labelKey}
                    to={item.href}
              onClick={mobile ? onClose : undefined}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-11 items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )
                    }
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    {t(item.labelKey)}
                  </NavLink>
                ))}
              </div>
            </>
          )}
        </nav>

        {/* User info */}
        <div className="flex items-center px-4 py-4 border-t border-gray-200">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
            <span className="text-sm font-medium text-primary-700">
              {user?.username.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.username}
            </p>
            <p className="text-xs text-gray-500">{t(user?.role === 'admin' ? 'common.admin' : 'common.user')}</p>
          </div>
        </div>
      </div>
  );
  return <>
    <aside className="fixed inset-y-0 left-0 z-50 hidden lg:flex lg:w-64 lg:flex-col">{content(false)}</aside>
    <dialog ref={dialog} id="mobile-navigation" aria-label={t('nav.mainMenu')} onClose={onClose} onClick={event => { if (event.target === event.currentTarget) onClose(); }}
      className="fixed inset-y-0 left-0 m-0 h-[100dvh] max-h-none w-80 max-w-[calc(100vw-2rem)] border-0 p-0 shadow-xl backdrop:bg-slate-950/50 lg:hidden">
      {mobileOpen && content(true)}
    </dialog>
  </>;
}
